import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import moment from 'moment';
import { VoiceVisualService } from './voice-visualization.service';

// ---------------- Web Speech minimal types ----------------
type WebSpeechRecognitionCtor = new () => WebSpeechRecognition;

interface WebSpeechRecognition {
    lang: string;
    continuous: boolean;
    interimResults: boolean;

    onresult: ((event: WebSpeechRecognitionEvent) => void) | null;
    onerror: ((event: any) => void) | null;
    onend: (() => void) | null;

    start(): void;
    stop(): void;
    abort(): void;
}
interface WebSpeechRecognitionEvent {
    resultIndex: number;
    results: WebSpeechRecognitionResultList;
}
interface WebSpeechRecognitionResultList {
    length: number;
    [index: number]: WebSpeechRecognitionResult;
}
interface WebSpeechRecognitionResult {
    isFinal: boolean;
    0: { transcript: string };
}

// ---------------- Public API ----------------
export type AssistantState = 'idle' | 'speaking' | 'listening' | 'stopping' | 'error';

export interface AssistantOptions {
    lang: string;             // 'de-DE', 'de-AT', ...
    silenceMs?: number;       // initial silence timeout (default 3000)
    partialResults?: boolean; // default true
    maxResults?: number;      // native only, default 1
    promptOnNative?: string;  // native prompt (optional)
    greetings?: { on: string; off: string };

    /**
     * After user has spoken once:
     * - null = no auto-deactivate anymore (recommended)
     * - number = timeout in ms between turns (e.g. 20000)
     */
    postSpeechSilenceMs?: number | null;
}

type NormalizedOptions =
    Required<Omit<AssistantOptions, 'postSpeechSilenceMs'>> & { postSpeechSilenceMs: number | null };

@Injectable({ providedIn: 'root' })
export class VoiceAssistantService {
    private readonly isNative = Capacitor.isNativePlatform();
    private webRec: WebSpeechRecognition | null = null;

    private readonly stateSub = new BehaviorSubject<AssistantState>('idle');
    private readonly activeSub = new BehaviorSubject<boolean>(false);
    private readonly partialSub = new BehaviorSubject<string>('');
    private readonly finalSub = new BehaviorSubject<string>('');
    private readonly utteranceSub = new Subject<string>();

    private opts: NormalizedOptions | null = null;

    private silenceTimer: number | null = null;
    private lastHeardAt = 0;
    private restarting = false;
    private stopRequested = false;

    // Native: keep last partial as final when listening stops
    private lastPartialNative = '';

    // Only auto-timeout before first user speech
    private hasHeardUser = false;

    // Prevent auto-restart loops while we stop STT to speak
    private suppressAutoRestart = false;

    // Native TTS voice cache
    private nativeVoiceIndexByLang = new Map<string, number>();
    private resolvingVoice = new Map<string, Promise<number | undefined>>();

    constructor(
        private visualService: VoiceVisualService
    ) {
        if (!this.isNative) this.webRec = this.createWebRecognizer();
        if (!this.isNative) this.hookWebVoicesWarmup();
    }

    // ---- streams ----
    state$(): Observable<AssistantState> { return this.stateSub.asObservable(); }
    active$(): Observable<boolean> { return this.activeSub.asObservable(); }
    partial$(): Observable<string> { return this.partialSub.asObservable(); }
    final$(): Observable<string> { return this.finalSub.asObservable(); }
    utterance$(): Observable<string> { return this.utteranceSub.asObservable(); }

    // ---- button API ----
    async toggle(options: AssistantOptions): Promise<void> {
        if (this.activeSub.value) await this.deactivate();
        else await this.activate(options);
    }

    /**
     * Speak text safely:
     * - pauses silence timer
     * - stops STT (so it won't hear itself)
     * - blocks auto-restart while stopped for TTS
     * - speaks
     * - resumes listening if active
     */
    async say(text: string, resumeListening = true): Promise<void> {
        this.clearSilenceTimer();

        // block auto-restart caused by stopRecognition() (native emits listeningState=stopped)
        this.suppressAutoRestart = true;

        try { await this.stopRecognition(); } catch { /* ignore */ }

        this.stateSub.next('speaking');
        this.visualService.onSpeakingStart(text);
        await this.speak(text);
        this.visualService.onSpeakingEnd();
        this.lastHeardAt = Date.now();

        // allow restarts again
        this.suppressAutoRestart = false;

        if (this.activeSub.value && resumeListening) {
            await this.startListeningCycle(false);
        } else if (!this.activeSub.value) {
            this.stateSub.next('idle');
        }
    }

    async activate(options: AssistantOptions): Promise<void> {
        if (this.activeSub.value) return;

        this.opts = {
            lang: options.lang,
            silenceMs: options.silenceMs ?? 3000,
            partialResults: options.partialResults ?? true,
            maxResults: options.maxResults ?? 1,
            promptOnNative: options.promptOnNative ?? '',
            greetings: options.greetings ?? { on: 'Servus! Neger zu diensten! Wie kann ich helfen?', off: 'donk`schihen, gute nocht!' },
            postSpeechSilenceMs: (options.postSpeechSilenceMs === undefined) ? null : options.postSpeechSilenceMs,
        };

        const available = await this.isAvailable();
        if (!available) {
            this.stateSub.next('error');
            throw new Error('Speech recognition not available');
        }

        this.activeSub.next(true);
        this.stopRequested = false;
        this.partialSub.next('');
        this.finalSub.next('');
        this.lastHeardAt = Date.now();
        this.hasHeardUser = false;


        // greet; say() resumes listening; do NOT call startListeningCycle again here
        await this.say(this.opts.greetings.on, true);
    }

    async deactivate(): Promise<void> {
        if (!this.activeSub.value) return;

        this.activeSub.next(false);
        this.stopRequested = true;
        this.stateSub.next('stopping');
        this.clearSilenceTimer();

        // prevent restart while we stop
        this.suppressAutoRestart = true;
        try { await this.stopRecognition(); } catch { /* ignore */ }
        this.suppressAutoRestart = false;

        const off = this.opts?.greetings.off ?? 'Bis zum nächsten Mal';
        await this.say(off, false);

        this.stateSub.next('idle');
    }

    // ---------------- Core logic ----------------
    private async startListeningCycle(resetText: boolean): Promise<void> {
        await this.visualService.startMic();
        if (!this.opts) return;
        if (!this.activeSub.value) return;

        if (resetText) {
            this.partialSub.next('');
            this.finalSub.next('');
        }

        this.stateSub.next('listening');
        this.lastHeardAt = Date.now();
        this.armSilenceTimer();
        await this.startWebContinuous(this.opts);

    }

    private armSilenceTimer(): void {
        if (!this.opts) return;
        this.clearSilenceTimer();

        // before user spoke: use initial silence timeout
        // after first user speech: disable by default (null), or use postSpeechSilenceMs
        const ms = (!this.hasHeardUser)
            ? this.opts.silenceMs
            : this.opts.postSpeechSilenceMs;

        if (ms === null) return;

        this.silenceTimer = window.setInterval(() => {
            if (!this.activeSub.value) return;
            if (this.stateSub.value === 'speaking') return;

            const silentFor = Date.now() - this.lastHeardAt;
            if (silentFor >= ms) void this.deactivate();
        }, 200);
    }

    private clearSilenceTimer(): void {
        if (this.silenceTimer !== null) {
            clearInterval(this.silenceTimer);
            this.silenceTimer = null;
        }
    }

    private markHeard(text: string, isFinal: boolean): void {
        this.lastHeardAt = Date.now();

        if (isFinal) {
            this.hasHeardUser = true;     // after first real user input: no more 3s auto-off
            this.finalSub.next(text);
            this.utteranceSub.next(text);
            this.armSilenceTimer();       // re-arm with postSpeech policy
        } else {
            this.partialSub.next(text);
        }
    }

    // ---------------- STT availability/permissions ----------------
    private async isAvailable(): Promise<boolean> {
        return this.webRec !== null;
    }

    private async stopRecognition(): Promise<void> {
        this.visualService.stopMic();

        this.webRec?.stop();
    }

    // ---------------- Web STT fallback ----------------
    private createWebRecognizer(): WebSpeechRecognition | null {
        const w = window as any;
        const Ctor: WebSpeechRecognitionCtor | undefined =
            (w.SpeechRecognition as WebSpeechRecognitionCtor | undefined) ||
            (w.webkitSpeechRecognition as WebSpeechRecognitionCtor | undefined);

        if (!Ctor) return null;

        const rec = new Ctor();
        rec.onresult = null;
        rec.onerror = null;
        rec.onend = null;
        rec.continuous = true;
        rec.interimResults = true;
        return rec;
    }

    private async startWebContinuous(opts: NormalizedOptions): Promise<void> {
        const rec = this.webRec;
        if (!rec) throw new Error('Web Speech API not available');

        rec.lang = opts.lang;
        rec.interimResults = opts.partialResults;
        rec.continuous = true;

        rec.onresult = (ev: WebSpeechRecognitionEvent) => {
            let interim = '';
            let final = '';

            for (let i = ev.resultIndex; i < ev.results.length; i++) {
                const transcript = (ev.results[i][0]?.transcript ?? '').toString();
                if (ev.results[i].isFinal) final += transcript;
                else interim += transcript;
            }

            const iText = interim.trim();
            const fText = final.trim();

            if (iText) this.markHeard(iText, false);
            if (fText) this.markHeard(fText, true);
        };

        rec.onerror = () => {
            this.stateSub.next('error');
            void this.deactivate();
        };

        rec.onend = () => {
            if (this.activeSub.value && !this.stopRequested && !this.suppressAutoRestart && this.stateSub.value !== 'speaking') {
                void this.startListeningCycle(false);
            }
        };

        try {
            rec.start();
        } catch {
            this.stateSub.next('error');
            void this.deactivate();
        }
    }

  

    private async speak(text: string): Promise<void> {
        const lang = this.opts?.lang ?? 'de-DE';

        // Web (PWA)
        if (!this.isNative && typeof window !== 'undefined' && 'speechSynthesis' in window) {
            await this.speakWeb(text, lang);
            return;
        } else {
            await this.speakWeb(text, lang);
            return;
        }

    }

    private hookWebVoicesWarmup(): void {
        if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
        const synth = window.speechSynthesis;
        try { synth.getVoices?.(); } catch { /* ignore */ }
        (synth as any).onvoiceschanged = () => { try { synth.getVoices?.(); } catch { } };
    }

    private pickNiceWebVoice(lang: string): SpeechSynthesisVoice | null {
        const synth = window.speechSynthesis;
        const voices = synth.getVoices?.() ?? [];
        const want = lang.toLowerCase();
        const base = want.split('-')[0];

        // nur passende Sprache
        const candidates = voices.filter(v => (v.lang || '').toLowerCase().startsWith(base));

        // Heuristik: bevorzugte Namen (kannst du erweitern)
        const preferredNameHints = [
            'female', 'woman', 'zira', 'susan', 'victoria', 'katja', 'anna', 'siri',
            'google', 'natural', 'neural', 'premium'
        ];

        // 1) exaktes lang + preferred hint
        const exact = candidates.filter(v => (v.lang || '').toLowerCase() === want);
        const bestExact = exact.find(v => preferredNameHints.some(h => v.name.toLowerCase().includes(h)));
        if (bestExact) return bestExact;

        // 2) preferred hint in candidates
        const best = candidates.find(v => preferredNameHints.some(h => v.name.toLowerCase().includes(h)));
        if (best) return best;

        // 3) fallback: erste Stimme der Sprache
        return candidates[0] ?? null;
    }

    private speakWeb(text: string, lang: string): Promise<void> {
        return new Promise((resolve) => {
            const synth = window.speechSynthesis;
            if (!synth) return resolve();

            const u = new SpeechSynthesisUtterance(text);
            u.lang = lang;

            const voice = this.pickNiceWebVoice(lang);
            if (voice) u.voice = voice;

            // “netter” wirkt oft mit etwas höherem pitch + leicht langsamer
            u.pitch = 1.5; // 0..2
            u.rate = 1.2;  // 0.1..10
            u.volume = 1.0;

            u.onend = () => resolve();
            u.onerror = () => resolve();

            synth.cancel();
            synth.speak(u);
        });
    }


    private sleep(ms: number): Promise<void> {
        return new Promise((r) => setTimeout(r, ms));
    }
}
